import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-expo";
import { Link, useFocusEffect } from "expo-router";
import { ActivityIndicator, FlatList, Pressable, StyleSheet, Text, View } from "react-native";

import { fetchShelf, type ShelfItem, type ShelfStatus } from "../../src/api";
import { formatDesigners, formatSubtitle } from "../../src/format";
import { CoverNotice, Thumbnail } from "../../src/cover";
import { colour, type } from "../../src/theme";

/**
 * Your shelf (WI-031).
 *
 * The first screen in the app that shows data belonging to the person looking
 * at it rather than the catalogue everyone shares.
 */

const FILTERS: { value: ShelfStatus | "all"; label: string }[] = [
  { value: "all", label: "All" },
  { value: "owned", label: "Owned" },
  { value: "wishlist", label: "Wishlist" },
];

export default function ShelfScreen() {
  const { isLoaded, isSignedIn } = useAuth();
  const [filter, setFilter] = useState<ShelfStatus | "all">("all");
  const [items, setItems] = useState<ShelfItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!isLoaded || !isSignedIn) return () => {};
    const controller = new AbortController();

    setError(null);
    fetchShelf({
      ...(filter === "all" ? {} : { status: filter }),
      signal: controller.signal,
    })
      .then((found) => {
        if (!controller.signal.aborted) setItems(found);
      })
      .catch((cause: Error) => {
        if (controller.signal.aborted || cause.name === "AbortError") return;
        setError(cause.message);
      });

    return () => controller.abort();
  }, [filter, isLoaded, isSignedIn]);

  useEffect(load, [load]);

  // Coming back from a game page where the shelf was changed should show the
  // change, not a list from before the tap.
  useFocusEffect(load);

  if (!isLoaded) return null;

  if (!isSignedIn) {
    return (
      <View style={styles.centre}>
        <Text style={styles.blurb}>Your shelf is yours, so you need to be signed in.</Text>
        <Link href="/sign-in" asChild>
          <Pressable style={styles.button}>
            <Text style={styles.buttonText}>Sign in</Text>
          </Pressable>
        </Link>
      </View>
    );
  }

  return (
    <View style={styles.screen}>
      <View style={styles.filters}>
        {FILTERS.map((f) => (
          <Pressable
            key={f.value}
            onPress={() => setFilter(f.value)}
            style={[styles.chip, filter === f.value && styles.chipActive]}
          >
            <Text style={[styles.chipText, filter === f.value && styles.chipTextActive]}>
              {f.label}
            </Text>
          </Pressable>
        ))}
      </View>

      {error !== null && <Text style={styles.error}>{error}</Text>}

      {items === null && error === null && <ActivityIndicator style={styles.spinner} />}

      {items !== null && items.length === 0 && (
        <Text style={styles.blurb}>
          Nothing here yet. Find a game and mark it owned or wanted.
        </Text>
      )}

      <CoverNotice shown={(items ?? []).some((g) => g.imageUrl)} />

      <FlatList
        data={items ?? []}
        keyExtractor={(item) => item.slug}
        contentContainerStyle={styles.list}
        renderItem={({ item }) => (
          <Link href={{ pathname: "/game/[slug]", params: { slug: item.slug } }} asChild>
            <Pressable style={styles.card}>
              <Thumbnail url={item.imageUrl} title={item.title} />
              <View style={styles.cardBody}>
              <View style={styles.cardHead}>
                <Text style={styles.title}>{item.title}</Text>
                <Text style={styles.badge}>{item.status === "owned" ? "Owned" : "Wishlist"}</Text>
              </View>
              <Text style={styles.subtitle}>{formatSubtitle(item)}</Text>
              <Text style={styles.designers}>{formatDesigners(item)}</Text>
              {item.rating != null && <Text style={styles.rating}>Your rating: {item.rating}/10</Text>}
              </View>
            </Pressable>
          </Link>
        )}
      />
    </View>
  );
}

const styles = StyleSheet.create({
  screen: { flex: 1, padding: 16 },
  centre: { flex: 1, alignItems: "center", justifyContent: "center", padding: 24, gap: 12 },
  filters: { flexDirection: "row", gap: 8, marginBottom: 12 },
  chip: {
    borderWidth: 1,
    borderColor: colour.lineStrong,
    borderRadius: 999,
    paddingVertical: 6,
    paddingHorizontal: 14,
    backgroundColor: colour.surface,
  },
  chipActive: { backgroundColor: colour.ink, borderColor: colour.ink },
  chipText: { ...type.meta, color: colour.inkMuted, fontWeight: "600" },
  chipTextActive: { color: colour.surface },
  spinner: { marginTop: 24 },
  blurb: { color: colour.inkMuted, ...type.meta, textAlign: "center" },
  error: { color: colour.danger, ...type.meta, marginBottom: 8 },
  list: { gap: 12, paddingBottom: 24 },
  cardBody: { flex: 1, gap: 2 },
  card: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    backgroundColor: colour.surface,
    borderRadius: 8,
    padding: 16,
    borderWidth: 1,
    borderColor: colour.line,
  },
  cardHead: { flexDirection: "row", justifyContent: "space-between", alignItems: "flex-start", gap: 8 },
  title: { ...type.title, fontWeight: "600", color: colour.ink, flexShrink: 1 },
  badge: { ...type.label, color: colour.inkMuted, textTransform: "uppercase", letterSpacing: 0.5 },
  subtitle: { ...type.meta, color: colour.inkMuted, marginTop: 4 },
  designers: { ...type.meta, color: colour.inkMuted, marginTop: 2 },
  rating: { ...type.meta, color: colour.ink, marginTop: 6, fontWeight: "600" },
  button: {
    backgroundColor: colour.ink,
    borderRadius: 8,
    paddingVertical: 12,
    paddingHorizontal: 24,
  },
  buttonText: { color: colour.surface, ...type.body, fontWeight: "600" },
});
